import { useId, useState, type ReactNode } from "react";
import { Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { ResultRow } from "../ResultRow";
import s from "./PvkAccordion.module.css";

export interface PvkAnalyteRow {
  analyteCode: string;
  label: string;
  value: number;
  unit: string;
  /** Already resolved for the caller's provenance/sex — a plain display
      string, never re-derived or compared here. */
  rangeText?: string;
  /** Caller-owned, passed through to the row untouched. */
  aside?: ReactNode;
}

export interface PvkAccordionProps {
  rows: PvkAnalyteRow[];
  /** Called when a sub-measurement is tapped. Omit on a surface with no
      explainer behind a reading, and the rows render untappable. */
  onRowOpened?: (analyteCode: string) => void;
  onToggled?: (expanded: boolean) => void;
}

// The blood count folds down to its first sub-measurement: enough to show the
// panel is populated, and the ordering is PVK_ANALYTE_ORDER's fixed clinical
// convention, not a ranking of this client's own values.
const PREVIEW_COUNT = 1;

/* The blood count as one row in the reading list, not as a card of its own.
 *
 * It used to be a nested ResultCard inside the card stack with a full-width
 * "show the whole blood count" bar under it — three pieces of chrome, 57px of
 * them the bar alone, to say one thing. Now the title row *is* the
 * disclosure: it sits in the same list as ferritin and CRP, at the same
 * rhythm, and the sub-measurements unfold underneath it as the same
 * ResultRow every other reading uses.
 *
 * The trailing count does not change with the state — a disclosure's state is
 * `aria-expanded` and a chevron's rotation, and a label that rewrites itself
 * ("show more" / "show less") makes the control's accessible name move under
 * anyone navigating by name. */
export function PvkAccordion({ rows, onRowOpened, onToggled }: PvkAccordionProps) {
  const { t } = useTranslation("labs");
  const [expanded, setExpanded] = useState(false);
  const rowsId = useId();

  if (rows.length === 0) {
    return null;
  }

  const canToggle = rows.length > PREVIEW_COUNT;
  const visibleRows = expanded || !canToggle ? rows : rows.slice(0, PREVIEW_COUNT);

  // Side effect stays out of the updater — StrictMode invokes updaters twice
  // and would double-count the event.
  function toggle() {
    setExpanded(!expanded);
    onToggled?.(!expanded);
  }

  const title = (
    <Text as="span" size="sm" weight="medium" className={s.title}>
      {t("pvk.title", "Perusverenkuva (PVK)")}
    </Text>
  );
  const count = (
    <Text as="span" size="xs" className={s.count}>
      {t("pvk.count", "{{count}} osatutkimusta", { count: rows.length })}
    </Text>
  );

  return (
    <div className={s.root} data-expanded={expanded}>
      {canToggle ? (
        <button
          type="button"
          className={s.disclosure}
          aria-expanded={expanded}
          aria-controls={rowsId}
          onClick={toggle}
        >
          {title}
          <span className={s.trailing}>
            {count}
            <svg
              className={s.chevron}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      ) : (
        <div className={s.disclosure}>
          {title}
          <span className={s.trailing}>{count}</span>
        </div>
      )}

      <div id={rowsId} className={s.sub}>
        {visibleRows.map((row) => (
          <ResultRow
            key={row.analyteCode}
            label={row.label}
            value={row.value}
            unit={row.unit}
            rangeText={row.rangeText}
            aside={row.aside}
            onClick={onRowOpened === undefined ? undefined : () => onRowOpened(row.analyteCode)}
          />
        ))}
      </div>
    </div>
  );
}
